import React, { useState } from 'react';
import { Layout, Menu, Drawer, Button, Tabs, FloatButton } from 'antd';
import { 
  MenuOutlined, 
  SwapOutlined, 
  SearchOutlined, 
  ToolOutlined,
  MonitorOutlined,
  BankOutlined,
  FireOutlined,
  HomeOutlined,
  CloseOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import type { MenuProps } from 'antd';

const { Header, Content } = Layout;
const { TabPane } = Tabs;

interface MobileLayoutProps {
  children: React.ReactNode;
  siderMenus: Record<string, MenuProps['items']>;
  selectedNav: string;
  selectedSide: string[];
}

const MobileLayout: React.FC<MobileLayoutProps> = ({ 
  children, 
  siderMenus, 
  selectedNav,
  selectedSide 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerVisible, setDrawerVisible] = useState(false);

  // 主导航项
  const navItems = [
    { key: 'chain', label: '链上交互', icon: <SwapOutlined /> },
    { key: 'query', label: '批量查询', icon: <SearchOutlined /> },
    { key: 'monitor', label: '监控', icon: <MonitorOutlined /> },
    { key: 'cex', label: '交易所', icon: <BankOutlined /> },
    { key: 'gas', label: '实时Gas', icon: <FireOutlined /> },
    { key: 'tools', label: '实用工具', icon: <ToolOutlined /> },
  ];

  // 处理主导航点击
  const handleNavClick = (key: string) => {
    if (key === 'chain') navigate('/chain');
    else if (key === 'query') navigate('/query');
    else if (key === 'monitor') navigate('/monitor');
    else if (key === 'cex') navigate('/cex');
    else if (key === 'gas') navigate('/gas');
    else if (key === 'tools') navigate('/tools');
    setDrawerVisible(false);
  };

  // 处理侧边栏菜单点击
  const handleSideMenuClick = ({ key }: { key: string }) => {
    navigate(key);
    setDrawerVisible(false);
  };

  // 获取当前页面标题
  const getCurrentTitle = () => {
    const navItem = navItems.find(item => item.key === selectedNav);
    return navItem?.label || '首页';
  };

  return (
    <Layout className="mobile-layout" style={{ height: '100vh' }}>
      {/* 移动端顶部导航 */}
      <Header 
        style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          padding: '0 var(--spacing-md)',
          height: '56px',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
        }}
      >
        {/* 左侧：汉堡菜单 */}
        <Button
          type="text"
          icon={<MenuOutlined />}
          onClick={() => setDrawerVisible(true)}
          style={{
            color: '#fff',
            fontSize: '18px',
            padding: '4px 8px',
            minWidth: 'auto'
          }}
        />

        {/* 中间：当前页面标题 */}
        <div style={{ 
          color: '#fff', 
          fontWeight: '500', 
          fontSize: 'var(--font-md)',
          textAlign: 'center',
          flex: 1
        }}>
          {getCurrentTitle()}
        </div>

        {/* 右侧：连接钱包按钮（简化版） */}
        <div style={{ 
          transform: 'scale(0.8)',
          transformOrigin: 'right center'
        }}>
          <ConnectButton 
            showBalance={false}
            chainStatus="icon"
            accountStatus="avatar"
          />
        </div>
      </Header>

      {/* 主内容区域 */}
      <Content 
        style={{ 
          marginTop: '56px', // 为固定头部留出空间
          padding: 'var(--spacing-sm)',
          paddingBottom: '80px', // 为底部导航留出空间
          background: '#f5f5f5',
          height: 'calc(100vh - 56px)',
          overflowY: 'auto'
        }}
      >
        {children}
      </Content>

      {/* 底部Tab导航 */}
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: '#fff',
          borderTop: '1px solid #f0f0f0',
          boxShadow: '0 -2px 12px rgba(0,0,0,0.08)',
          zIndex: 1000,
          padding: '8px 0 4px 0'
        }}
      >
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-around',
          alignItems: 'center',
          maxWidth: '500px',
          margin: '0 auto'
        }}>
          {navItems.map((item) => (
            <div
              key={item.key}
              onClick={() => handleNavClick(item.key)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '6px 12px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                borderRadius: '8px',
                minWidth: '64px'
              }}
            >
              <div style={{ 
                fontSize: '20px',
                color: selectedNav === item.key ? '#1890ff' : '#666',
                marginBottom: '2px',
                transition: 'color 0.2s ease'
              }}>
                {item.icon}
              </div>
              <span style={{ 
                fontSize: '11px',
                color: selectedNav === item.key ? '#1890ff' : '#666',
                fontWeight: selectedNav === item.key ? '500' : '400',
                transition: 'all 0.2s ease'
              }}>
                {item.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* 抽屉式侧边栏 */}
      <Drawer
        title={
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            marginRight: '-16px'
          }}>
            <div>
              <HomeOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
              DDDD Tools
            </div>
            <Button 
              type="text" 
              icon={<CloseOutlined />}
              onClick={() => setDrawerVisible(false)}
              style={{ 
                color: '#666',
                padding: '4px'
              }}
            />
          </div>
        }
        placement="left"
        closable={false}
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        width={280}
        styles={{
          body: { padding: 0 }
        }}
      >
        {/* 当前选中的主导航标题 */}
        <div style={{
          padding: 'var(--spacing-md)',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: '#fff',
          marginBottom: 'var(--spacing-sm)'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center',
            fontSize: 'var(--font-md)',
            fontWeight: '500'
          }}>
            {navItems.find(item => item.key === selectedNav)?.icon}
            <span style={{ marginLeft: '8px' }}>
              {getCurrentTitle()}
            </span>
          </div>
          <div style={{ 
            fontSize: 'var(--font-xs)',
            opacity: 0.9,
            marginTop: '4px'
          }}>
            选择具体功能
          </div>
        </div>

        {/* 侧边栏菜单 */}
        <Menu
          mode="inline"
          selectedKeys={selectedSide}
          items={siderMenus[selectedNav] || []}
          onClick={handleSideMenuClick}
          style={{ 
            border: 'none',
            background: 'transparent'
          }}
        />

        {/* 底部额外信息 */}
        <div style={{
          position: 'absolute',
          bottom: '20px',
          left: '16px',
          right: '16px',
          padding: '12px',
          background: '#f8f9fa',
          borderRadius: '8px',
          fontSize: 'var(--font-xs)',
          color: '#666',
          textAlign: 'center'
        }}>
          <div style={{ marginBottom: '4px', fontWeight: '500' }}>
            🤖 99% Built with AI
          </div>
          <div>Web3 开发工具集</div>
        </div>
      </Drawer>

      {/* 快速返回顶部按钮 */}
      <FloatButton.BackTop 
        style={{ 
          right: 16,
          bottom: 90 // 避免与底部导航重叠
        }}
      />
    </Layout>
  );
};

export default MobileLayout; 